// PDF through the browser's own viewer. Every major browser renders a PDF in
// an iframe, with page navigation, search and zoom of its own, so the shell
// adds what the built-in viewer cannot: print, details, download, and an
// honest state where the browser has switched inline PDFs off.

import { useEffect, useRef, type ReactNode } from 'react';
import { Notice } from '../chrome.js';
import type { RendererProps } from '../types.js';

function inlinePdfEnabled(): boolean {
  if (typeof navigator === 'undefined') return true;
  const flag = (navigator as Navigator & { pdfViewerEnabled?: boolean }).pdfViewerEnabled;
  // Undefined on browsers that predate the property (Safari); they render PDFs.
  return flag !== false;
}

export function PdfRenderer({ file, labels, register, download, openExternally }: RendererProps): ReactNode {
  const frame = useRef<HTMLIFrameElement | null>(null);
  const supported = inlinePdfEnabled() && !!file.objectUrl;

  useEffect(() => {
    register({
      capabilities: { print: supported, fullscreen: true },
      actions: {
        print: () => {
          try {
            frame.current?.contentWindow?.focus();
            frame.current?.contentWindow?.print();
          } catch {
            if (file.objectUrl) window.open(file.objectUrl, '_blank', 'noopener');
          }
        }
      },
      details: [{ label: labels.detailRenderer, value: supported ? labels.rendererNative : labels.rendererNone }]
    });
  }, [supported, file.objectUrl, labels, register]);

  if (!supported) {
    return (
      <Notice family="pdf" title={labels.unsupportedTitle} message={labels.unsupportedPdfInline} labels={labels}
        download={download} openExternally={openExternally} />
    );
  }

  return (
    <div className="rr-fv__canvas rr-fv__canvas--pdf">
      <iframe
        ref={frame}
        className="rr-fv__pdf"
        src={`${file.objectUrl}#toolbar=1&navpanes=0`}
        title={file.name}
      />
    </div>
  );
}
